/** @jsx jsx */
import { jsx } from "@emotion/react";
import { FC, Fragment, ReactElement } from "react";
import { DISPLAY_AS_TRANSLATIONS } from "src/components/molecules/modals/TranslationsModal/constants";
import {
  ColumnTranslKeys,
  OnChangeTranslations,
  OnChangeTranslationsByKeys,
  Translations,
  TranslationsByKeys,
} from "src/components/molecules/modals/TranslationsModal/types";
import SingleValueRelation from "src/components/molecules/SingleValueRelation";
import useGetFormattedDecimalValue from "src/hooks/table/cells/useGetFormattedDecimalValue/useGetFormattedDecimalValue";
import { noop } from "../../../../../globalConsts";
import {
  DateType,
  FlowioInputType as InputType,
  FlowioInputValue,
  TemplateId,
} from "../../../../../globalTypes";
import {
  FlowioFile,
  TableCellValue,
  TableColumn,
  TableColumnInputType as ColInputType,
  TableColumns,
  TableRow,
} from "../../../../../requests/table/types";
import {
  ColumnFormatDict,
  ValueFormat,
} from "../../../../../requests/templates/types";
import {
  flowioInputValueToTableCellValue,
  tableCells2valueDict,
} from "../../../../../utils/table";
import FileList from "../../../../molecules/FileList";
import {
  parseDetailLink,
  parseLinkValue,
} from "../../../../organisms/TableData/TableDataDesktop/TableDataRow/TableDataCell/utils";
import FlowioLinkToDetail from "../../FlowioLink";
import TableCellArrayInput from "../../TableCellArrayInput";
import FlowioInput from "../FlowioInput";
import {
  determineDateView,
  getTranslationsAndColKeyForColumn,
  matchSelectItem,
} from "./utils";

type Props = {
  column: TableColumn;
  value: TableCellValue;
  unformattedValue: TableCellValue;
  format: ValueFormat;
  editable: boolean;
  onChange?: (v: TableCellValue) => void;
  onBlur?(): void;
  templateId?: TemplateId | null;
  columns?: TableColumns;
  columnsFormat?: ColumnFormatDict;
  row?: TableRow;
  refetchRowCallback?: () => void;
  translationsByKeys?: TranslationsByKeys;
  onChangeTranslationsByKeys?: OnChangeTranslationsByKeys;
};

const UndecoratedFlowioTableCellInput: FC<Props> = (props) => {
  const getFormattedDecimalValue = useGetFormattedDecimalValue();

  const { column, value, templateId, row, refetchRowCallback } = props;
  const {
    editable = false,
    format,
    columns,
    onChange = noop,
    onBlur,
    unformattedValue,
    translationsByKeys,
    onChangeTranslationsByKeys,
  } = props;

  const required = format?.general?.required && editable;

  const { selectItems, inputType, isArray } = column;

  const handleChange = (v: FlowioInputValue): void => {
    onChange(flowioInputValueToTableCellValue(v));
  };

  if (isArray) {
    return <TableCellArrayInput {...props} />;
  }

  const renderTextarea = (): ReactElement => (
    <FlowioInput
      type={InputType.Textarea}
      value={value as string}
      config={{
        minLength: column.minLength ?? undefined,
        maxLength: column.maxLength ?? undefined,
      }}
      onChange={handleChange}
      onBlur={onBlur}
      noInputMode={!editable}
      required={required}
      disabled={!editable}
    />
  );

  switch (inputType) {
    case ColInputType.Checkbox: {
      return (
        <FlowioInput
          type={InputType.Checkbox}
          value={!!value}
          disabled={!editable}
          onChange={handleChange}
          onBlur={onBlur}
        />
      );
    }
    case ColInputType.DateFrom:
    case ColInputType.DateTo:
    case ColInputType.DatetimeLocal: {
      const dateVal = editable ? value : unformattedValue || value;
      return (
        <FlowioInput
          type={InputType.Datetime}
          format={format}
          value={dateVal as DateType}
          dateFormat={determineDateView(format)}
          noInputMode={!editable}
          onChange={handleChange}
          onBlur={onBlur}
          config={{
            min: format?.typeSpecific.date?.minValue,
            max: format?.typeSpecific.date?.maxValue,
          }}
          required={required}
        />
      );
    }

    case ColInputType.Decimal:
    case ColInputType.Number: {
      // protoze kdyz precastujeme value na Number, tak z null se stane 0 a tim padem
      // prestane fungovat required atribut a form kontrola bude brat input jako ze ma hodnotu
      const numberOrNullValue = value === null ? null : Number(value);
      return (
        <FlowioInput
          type={InputType.Number}
          value={numberOrNullValue}
          inputFormatConfig={{
            minValue: format?.typeSpecific.number?.minValue,
            maxValue: format?.typeSpecific.number?.maxValue,
          }}
          format={format}
          noInputMode={!editable}
          onChange={handleChange}
          onBlur={onBlur}
          required={required}
        />
      );
    }

    case ColInputType.HegFile:
    case ColInputType.File: {
      if (!templateId || !value) return null;

      return (
        <FileList
          fileValue={value as FlowioFile}
          templateId={templateId}
          templateItemId={column.templateItemId}
          disabled={!editable}
          row={row}
          disabledButton={row?.keyValue === null || row?.keyValue === -1}
          afterFileUploadCallback={refetchRowCallback}
        />
      );
    }

    case ColInputType.Hidden: {
      return (
        <FlowioInput
          type={InputType.Hidden}
          value={String(value)}
          onChange={handleChange}
          onBlur={onBlur}
        />
      );
    }

    case ColInputType.Link: {
      const parsedValue = parseLinkValue(value as string);
      const formattedValue = getFormattedDecimalValue(parsedValue, format);

      return (
        <Fragment>
          {parsedValue === "0" ? (
            <span>{String(formattedValue)}</span>
          ) : (
            <FlowioLinkToDetail
              value={formattedValue || ""}
              linkTo={parseDetailLink(
                value as string,
                templateId,
                column.templateItemId
              )}
            />
          )}
        </Fragment>
      );
    }

    case ColInputType.OverviewSelect: {
      const { overviewSelectTemplateId } = column;

      if (!overviewSelectTemplateId) {
        return <span>{String(value)}</span>;
      }

      return (
        <SingleValueRelation
          value={value as string | number | null}
          disabled={!editable}
          templateId={overviewSelectTemplateId}
          onChange={onChange}
          callbackAfterOnChangeFinish={(): void => onBlur?.()}
          required={required}
          rowValues={tableCells2valueDict(row?.cells)}
          detailTemplateId={row?.templateId}
        />
      );
    }

    case ColInputType.Category: {
      return <span>{String(value)}</span>;
    }

    case ColInputType.Select: {
      const isSearchSelect =
        format?.typeSpecific?.select?.displayAsSearchSelect;

      const getValue = (): string => {
        if (editable) return value as string;
        const matchedOption = matchSelectItem(selectItems, value as string);
        return matchedOption?.value ?? (value as string);
      };

      return (
        <FlowioInput
          type={
            (isSearchSelect
              ? InputType.SelectWithSearch
              : InputType.Select) as InputType.Select
          }
          value={getValue()}
          options={selectItems}
          noInputMode={!editable}
          onChange={handleChange}
          onBlur={onBlur}
          required={required}
          emptyOption
        />
      );
    }

    case ColInputType.Title:
    case ColInputType.Text: {
      const renderAsTextarea = !!format?.typeSpecific.text?.asTextArea;
      if (renderAsTextarea) return renderTextarea();

      const asPhoneOrMail = !!format?.typeSpecific.text?.asPhoneOrMail;

      // not very nice way of handling translations, but its only for the sake of BasicTemplateInfo.tsx component
      const translations = getTranslationsAndColKeyForColumn(
        translationsByKeys,
        column
      );

      const handleChangeTranslations: OnChangeTranslations = (trans) => {
        if (!onChangeTranslationsByKeys || !translations?.colKey) return;

        onChangeTranslationsByKeys(trans, translations.colKey);
      };

      return (
        <FlowioInput
          type={InputType.Text}
          value={value as string}
          unformattedValue={unformattedValue as string}
          interpretPhoneAndEmail={asPhoneOrMail}
          onChange={handleChange}
          onBlur={onBlur}
          format={format}
          columns={columns}
          columnsFormat={props.columnsFormat}
          noInputMode={!editable}
          config={{
            maxLength: column.maxLength ?? undefined,
          }}
          required={required}
          limitLongWords={column.limitLongWords}
          translations={translations?.translations}
          onChangeTranslation={
            translations ? handleChangeTranslations : undefined
          }
        />
      );
    }

    case ColInputType.Icon: {
      return (
        <FlowioInput
          type={InputType.Icon}
          value={value as string}
          noInputMode
        />
      );
    }

    case ColInputType.Textarea: {
      return renderTextarea();
    }

    default:
      return null;
  }
};

export default UndecoratedFlowioTableCellInput;
export type FlowioTableCellInputProps = Props;
